import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, ChevronRight, Shield, Zap, Mountain, Anchor,
  CheckCircle2, Phone, Mail, Download, Layers, BarChart3
} from 'lucide-react';
import { ROUTE_PATHS } from '@/lib/index';
import { IMAGES } from '@/assets/images';

/* ── 浅色工业风色值 ── */
const ORANGE = 'oklch(0.66 0.19 42)';
const CARD_BG = '#FFFFFF';
const BORDER = '#D0D5E2';
const MUTED_TEXT = '#5A6780';
const DEEP_BG = '#F0F2F5';

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};
const stagger = { show: { transition: { staggerChildren: 0.08 } } };

/* ── 技术参数 ── */
const techParams = [
  { test: '拉伸强度', standard: 'ISO 37-2011 / ASTM D412', value: '≥18 MPa', note: '高耐磨配方实测值' },
  { test: '断裂伸长率', standard: 'ISO 37-2011 / ASTM D412', value: '≥650%', note: '高弹性配方' },
  { test: '硬度', standard: 'ISO 48-2010 / ASTM D2240', value: '55±5 Shore A', note: '可定制 40A～70A' },
  { test: '比重', standard: 'ISO 2781-2008', value: '1.12～1.20 g/cm³', note: '依配方略有差异' },
  { test: '工作温度范围', standard: '—', value: '-50°C 至 +80°C', note: '短期接触可至 +100°C' },
  { test: '阿克隆耐磨量', standard: 'GB/T 1689', value: '≤0.5 cm³/1.61km', note: '高耐磨配方' },
  { test: '回弹率', standard: 'ISO 4662 / ASTM D1054', value: '≥65%', note: '室温测试，弹性优良' },
  { test: '撕裂强度', standard: 'ISO 34 / ASTM D624', value: '≥50 kN/m', note: '适应尖锐矿石冲击' },
];

/* ── 核心特性 ── */
const coreFeatures = [
  {
    icon: Mountain,
    title: '卓越耐磨性',
    titleEn: 'ABRASION RESISTANCE',
    desc: '高耐磨配方阿克隆磨耗量低至 0.5 cm³/1.61km，远优于合成橡胶，在矿山输送、溜槽衬里等高磨耗场合中表现突出，使用寿命显著延长。',
  },
  {
    icon: Zap,
    title: '高弹减震性能',
    titleEn: 'ELASTICITY & DAMPING',
    desc: '天然橡胶回弹率超过 65%，结合高断裂伸长率（≥650%），在冲击吸收和减震缓冲方面具有合成橡胶难以企及的优势，适合矿山大块矿石冲击场合。',
  },
  {
    icon: Shield,
    title: '高拉伸撕裂强度',
    titleEn: 'TEAR STRENGTH',
    desc: '拉伸强度可达 18 MPa 以上，撕裂强度 ≥50 kN/m，确保在矿石锋利棱角、高落差冲击以及重型载荷连续作业中，板材不发生撕裂破坏。',
  },
  {
    icon: Layers,
    title: '宽低温适用性',
    titleEn: 'LOW TEMPERATURE FLEX',
    desc: '最低工作温度可达 -50°C，在极寒矿山和港口环境中仍保持良好弹性与柔韧性，不脆化断裂，适合高寒地区全天候室外作业。',
  },
  {
    icon: BarChart3,
    title: '天然环保材料',
    titleEn: 'ECO-FRIENDLY',
    desc: '以天然乳胶为主要原料，生物基来源，在生命周期评估中碳排放强度低于主流合成橡胶。可满足部分行业对绿色供应链和环保材料认证的要求。',
  },
];

/* ── 应用场景 ── */
interface AppItem {
  num: string;
  title: string;
  titleEn: string;
  desc: string;
  details: string[];
  img: string | null;
  imgAlt: string;
}

const applications: AppItem[] = [
  {
    num: '①',
    title: '矿山输送带衬板',
    titleEn: 'MINING CONVEYOR LINING',
    desc: '矿山输送机的承载托辊区、转运站落料点和头尾滚筒等高磨耗部位，需要承受大块矿石的反复冲击和持续研磨。NR 高耐磨配方的低磨耗量和高撕裂强度，使其成为矿山输送系统衬板的优选材料，可显著延长更换周期，降低停机维护成本。',
    details: ['输送机承载段衬板', '转运站落料缓冲床', '头尾滚筒包胶层', '导料槽内衬'],
    img: IMAGES.P15_NR_MINING_1_183,
    imgAlt: '矿山输送带NR衬板应用',
  },
  {
    num: '②',
    title: '物料溜槽与导料槽衬里',
    titleEn: 'CHUTE & HOPPER LINING',
    desc: '矿石、煤炭等散料在溜槽中高速滑落时，对衬里材料产生强烈的冲击和划切磨损。NR 高弹性可有效吸收冲击能量，减少物料反弹和溅射，同时高耐磨表面大幅降低磨耗速率，延长衬板使用寿命。',
    details: ['矿石溜槽内壁衬里', '煤仓漏斗衬板', '矿车底板耐磨衬垫', '破碎机进料溜槽'],
    img: IMAGES.P17_MINE_CHUTE_1_173,
    imgAlt: '矿山溜槽NR衬里应用',
  },
  {
    num: '③',
    title: '减震缓冲垫与隔振底座',
    titleEn: 'VIBRATION ISOLATION',
    desc: '重型矿山设备、振动筛、破碎机及港口装卸机械底部，需要高效的减震隔振系统以降低设备振动对厂房结构的影响并延长设备使用寿命。NR 的高回弹率和宽频减振特性，使其成为工业减震垫和隔振底座的核心材料。',
    details: ['振动筛隔振底垫', '破碎机减震底座', '重型压力机隔振垫', '矿山风机减震支撑'],
    img: IMAGES.P15_NR_VIBRATION_1_197,
    imgAlt: '振动筛减震NR垫应用',
  },
  {
    num: '④',
    title: '港口码头防护与护舷',
    titleEn: 'PORT & DOCK PROTECTION',
    desc: '港口码头护舷、船舶靠泊缓冲件及装卸设备防护装置，需要材料在海洋盐雾环境中具备优异的弹性恢复能力和耐冲击性。NR 高弹性橡胶在承受船体碰撞冲击后能迅速恢复形状，配合良好的耐疲劳性能，适合港口高频次使用环境。',
    details: ['码头护舷缓冲垫', '船坞靠泊缓冲器', '装卸臂防护垫', '港口护脚橡胶块'],
    img: IMAGES.P17_NR_PORT_1_1_212,
    imgAlt: '港口码头NR防护应用',
  },
  {
    num: '⑤',
    title: '桥梁支座与建筑减振',
    titleEn: 'BRIDGE BEARINGS',
    desc: '公路和铁路桥梁的橡胶支座（板式支座）需要在数十年服务周期内持续承受上部结构荷载，同时允许水平位移和转角。NR 板式支座以其优越的弹性、蠕变小和耐疲劳性能，成为工程结构中使用最广的橡胶支座基础材料。',
    details: ['公路桥梁板式支座', '铁路桥梁减震支座', '建筑隔震垫', '管道支架弹性垫'],
    img: IMAGES.P15_NR_BRIDGE_1_198,
    imgAlt: '桥梁支座NR减震应用',
  },
];

/* ── 与主要合成橡胶对比 ── */
const comparison = [
  { prop: '耐磨性', nr: '★★★★★', nbr: '★★★☆☆', epdm: '★★★☆☆', fkm: '★★★★☆' },
  { prop: '回弹率', nr: '★★★★★', nbr: '★★★☆☆', epdm: '★★★★☆', fkm: '★★★☆☆' },
  { prop: '低温柔性', nr: '★★★★★', nbr: '★★★★☆', epdm: '★★★★☆', fkm: '★★☆☆☆' },
  { prop: '耐油性', nr: '★☆☆☆☆', nbr: '★★★★★', epdm: '★☆☆☆☆', fkm: '★★★★★' },
  { prop: '耐候性', nr: '★★☆☆☆', nbr: '★★☆☆☆', epdm: '★★★★★', fkm: '★★★★★' },
  { prop: '拉伸强度', nr: '★★★★★', nbr: '★★★★☆', epdm: '★★★☆☆', fkm: '★★★★☆' },
];

/* ── 定制能力 ── */
const customFormulas = [
  { dim: '耐磨等级', standard: '高耐磨（HА）', custom: '可强化至超耐磨（SHА）配方', use: '极端磨耗矿山场合' },
  { dim: '硬度', standard: '55±5 Shore A', custom: '可向 40A 或 70A 调整', use: '软垫片或高承载支座' },
  { dim: '低温等级', standard: '-50°C 柔性保证', custom: '可评估至 -60°C（极寒配方）', use: '极寒地区户外作业' },
  { dim: '阻燃性', standard: '标准（无阻燃）', custom: '可添加阻燃剂体系', use: '矿井内阻燃要求场合' },
  { dim: '颜色', standard: '黑色', custom: '米色/自然色（特定配方）', use: '特殊标识或食品接触' },
  { dim: '厚度 / 幅宽', standard: '2.0～20mm / 标准幅宽', custom: '可定制裁切尺寸', use: '大型衬板或异形件' },
];

/* ── 产品规格 ── */
const specList = [
  { label: '颜色', value: '黑色（标准）；米色/自然色（特定配方）' },
  { label: '厚度范围', value: '2.0mm 至 20.0mm' },
  { label: '幅宽', value: '0.91m 至 2m（标准），可定制' },
  { label: '硬度区间', value: '40A ～ 70A Shore A（可定制）' },
  { label: '耐磨等级', value: '高耐磨（HA）/ 超耐磨（SHA）配方可选' },
  { label: '阻燃选项', value: '标准版 / 阻燃版（矿井专用）' },
  { label: '定制服务', value: '裁切、定制尺寸及特殊配方开发' },
];

export default function ProductNR() {
  return (
    <div className="bg-background text-foreground min-h-screen">

      {/* ── 面包屑 ── */}
      <div className="border-b border-border" style={{ background: DEEP_BG }}>
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-2 text-xs" style={{ color: MUTED_TEXT }}>
            <Link to="/" className="hover:text-primary transition-colors">首页</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={ROUTE_PATHS.PRODUCTS} className="hover:text-primary transition-colors">产品系列</Link>
            <ChevronRight className="w-3 h-3" />
            <span style={{ color: ORANGE }}>NR 天然橡胶板</span>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden" style={{ background: DEEP_BG, minHeight: 420 }}>
        <img
          src={IMAGES.P15_MINING_1_214}
          alt="矿山高耐磨天然橡胶应用背景"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 0.13 }}
        />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(135deg, rgba(15,30,53,0.90) 0%, rgba(15,30,53,0.65) 100%)' }}
        />
        <div className="relative container mx-auto px-4 py-16 lg:py-24">
          <motion.div initial="hidden" animate="show" variants={stagger}>
            <motion.div variants={fadeUp} className="mb-8">
              <Link
                to={ROUTE_PATHS.PRODUCTS}
                className="inline-flex items-center gap-2 text-sm transition-colors"
                style={{ color: MUTED_TEXT }}
              >
                <ArrowLeft className="w-4 h-4" />
                返回产品列表
              </Link>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* 左：文字 */}
              <div>
                <motion.div variants={fadeUp} className="flex items-center gap-3 mb-4">
                  <span
                    className="px-3 py-1 text-xs font-black tracking-[0.2em] uppercase rounded-sm"
                    style={{ background: 'oklch(0.66 0.19 42 / 0.15)', color: ORANGE, border: `1px solid ${ORANGE}` }}
                  >
                    NR
                  </span>
                  <span className="text-xs tracking-widest uppercase" style={{ color: MUTED_TEXT }}>
                    NATURAL RUBBER · HIGH ABRASION FORMULA
                  </span>
                </motion.div>

                <motion.h1 variants={fadeUp} className="text-4xl lg:text-5xl font-black mb-4 leading-tight">
                  <span style={{ color: ORANGE }}>NR</span> 天然橡胶板
                  <span className="block text-2xl lg:text-3xl mt-1" style={{ color: 'oklch(0.70 0.015 225)' }}>
                    高耐磨配方
                  </span>
                </motion.h1>
                <motion.p variants={fadeUp} className="text-lg font-medium mb-2" style={{ color: 'oklch(0.75 0.010 225)' }}>
                  矿山输送与重工业耐冲击减震的顶级选材
                </motion.p>
                <motion.p variants={fadeUp} className="text-sm leading-relaxed mb-8" style={{ color: MUTED_TEXT }}>
                  NR 天然橡胶以卓越的耐磨性、高弹减震和超强撕裂强度，成为矿山输送衬板、溜槽衬里、
                  桥梁支座及港口防护等重工业场合的首选板材。高耐磨配方阿克隆磨耗量低至 0.5 cm³/1.61km，
                  同时具备优异的低温柔性（-50°C），适应极端作业环境。
                </motion.p>

                <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                  <Link
                    to={ROUTE_PATHS.CONTACT}
                    className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold rounded-sm"
                    style={{ background: ORANGE, color: '#FFFFFF' }}
                  >
                    <Phone className="w-4 h-4" />
                    立即询价
                  </Link>
                  <Link
                    to={`${ROUTE_PATHS.TECH_SPECS}#nr`}
                    className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold rounded-sm"
                    style={{ border: `1px solid ${BORDER}`, color: 'oklch(0.88 0.008 220)' }}
                  >
                    查看技术参数
                  </Link>
                </motion.div>
              </div>

              {/* 右：图 + 规格卡 */}
              <motion.div variants={fadeUp} className="flex flex-col gap-4">
                <div className="rounded overflow-hidden" style={{ border: `1px solid ${BORDER}` }}>
                  <img
                    src={IMAGES.P15_NR_SHEET_1_206}
                    alt="NR天然橡胶板材实物"
                    className="w-full object-cover"
                    style={{ maxHeight: 260, objectPosition: 'center' }}
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: '工作温度', value: '-50°C ~ +80°C', sub: '极寒环境全天候适用' },
                    { label: '拉伸强度', value: '≥18 MPa', sub: '六大主流橡胶中最高' },
                    { label: '阿克隆磨耗', value: '≤0.5 cm³', sub: '高耐磨配方实测值' },
                  ].map((s) => (
                    <div
                      key={s.label}
                      className="rounded p-3 text-center"
                      style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
                    >
                      <p className="text-[10px] tracking-widest uppercase mb-1" style={{ color: MUTED_TEXT }}>{s.label}</p>
                      <p className="text-sm font-bold leading-tight" style={{ color: ORANGE }}>{s.value}</p>
                      <p className="text-[10px] mt-0.5" style={{ color: MUTED_TEXT }}>{s.sub}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 核心特性 ── */}
      <section className="py-16 lg:py-20" style={{ background: '#F0F2F5' }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>CORE PROPERTIES</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">核心特性</h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {coreFeatures.map((f) => {
                const Icon = f.icon;
                return (
                  <motion.div
                    key={f.title}
                    variants={fadeUp}
                    className="rounded p-5 flex flex-col gap-3"
                    style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
                  >
                    <div
                      className="w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0"
                      style={{ background: 'oklch(0.66 0.19 42 / 0.12)', border: '1px solid oklch(0.66 0.19 42 / 0.3)' }}
                    >
                      <Icon className="w-5 h-5" style={{ color: ORANGE }} />
                    </div>
                    <div>
                      <p className="text-[9px] font-bold tracking-widest uppercase mb-0.5" style={{ color: MUTED_TEXT }}>{f.titleEn}</p>
                      <p className="text-sm font-bold mb-2">{f.title}</p>
                      <p className="text-xs leading-relaxed" style={{ color: MUTED_TEXT }}>{f.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 技术参数 ── */}
      <section id="tech-params" className="py-16 lg:py-20" style={{ background: DEEP_BG }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>TECHNICAL DATA</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">NR 高耐磨配方物理性能数据</h2>
              <p className="text-sm text-center mt-2" style={{ color: MUTED_TEXT }}>
                所有数值均为高耐磨（HA）配方典型实测值，依据 ISO / ASTM / GB/T 标准体系测试
              </p>
            </motion.div>

            {/* 核心指标 */}
            <motion.div variants={fadeUp} className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[
                { label: '拉伸强度', value: '≥18 MPa', sub: 'ISO 37 / ASTM D412' },
                { label: '断裂伸长率', value: '≥500%', sub: '高弹配方' },
                { label: '阿克隆磨耗', value: '≤0.5 cm³', sub: 'GB/T 1689 高耐磨配方' },
                { label: '低温使用下限', value: '-50°C', sub: '保持柔韧不脆化' },
              ].map((m) => (
                <div
                  key={m.label}
                  className="rounded p-5 text-center"
                  style={{ background: CARD_BG, border: '1px solid oklch(0.66 0.19 42 / 0.25)' }}
                >
                  <p className="text-3xl font-black mb-1" style={{ color: ORANGE }}>{m.value}</p>
                  <p className="text-sm font-semibold mb-1">{m.label}</p>
                  <p className="text-[10px] font-mono" style={{ color: MUTED_TEXT }}>{m.sub}</p>
                </div>
              ))}
            </motion.div>

            {/* 完整参数表 */}
            <motion.div variants={fadeUp} className="overflow-x-auto rounded" style={{ border: `1px solid ${BORDER}` }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: '#1A2337', borderBottom: `2px solid ${BORDER}` }}>
                    {['测试项目', '测试标准', '典型数值', '参考说明'].map((h) => (
                      <th key={h} className="px-5 py-3.5 text-left text-xs font-black tracking-widest uppercase" style={{ color: ORANGE }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {techParams.map((row, i) => (
                    <tr
                      key={row.test}
                      style={{ background: i % 2 === 0 ? CARD_BG : '#F0F2F5', borderBottom: `1px solid ${BORDER}` }}
                    >
                      <td className="px-5 py-3.5 font-semibold">{row.test}</td>
                      <td className="px-5 py-3.5 font-mono text-xs" style={{ color: MUTED_TEXT }}>{row.standard}</td>
                      <td className="px-5 py-3.5 font-bold" style={{ color: ORANGE }}>{row.value}</td>
                      <td className="px-5 py-3.5 text-xs" style={{ color: MUTED_TEXT }}>{row.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </motion.div>

            <motion.div variants={fadeUp} className="mt-5 flex items-start gap-2 text-xs" style={{ color: MUTED_TEXT }}>
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: ORANGE }} />
              <span>以上数据为高耐磨（HA）配方典型实测值，实际数据因配方调整而略有差异。如需标准配方或超耐磨（SHA）配方技术数据表，请联系我们获取。</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── 应用场景 ── */}
      <section className="py-16 lg:py-20" style={{ background: '#F0F2F5' }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-12">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>NR APPLICATIONS</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">经过验证的主要应用领域</h2>
            </motion.div>

            <div className="space-y-8">
              {applications.map((app, idx) => (
                <motion.div
                  key={app.num}
                  variants={fadeUp}
                  className="rounded overflow-hidden"
                  style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
                >
                  <div className={`grid grid-cols-1 lg:grid-cols-2 ${idx % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                    <div
                      className={`relative overflow-hidden ${idx % 2 === 1 ? 'lg:order-2' : ''}`}
                      style={{ minHeight: 260 }}
                    >
                      {app.img ? (
                        <img
                          src={app.img}
                          alt={app.imgAlt}
                          className="absolute inset-0 w-full h-full object-cover"
                          style={{ opacity: 0.88 }}
                        />
                      ) : (
                        <div
                          className="absolute inset-0 flex flex-col items-center justify-center"
                          style={{ background: '#ECEEF2', border: `2px dashed ${BORDER}` }}
                        >
                          <p className="text-xs font-mono" style={{ color: MUTED_TEXT }}>图片位置保留</p>
                        </div>
                      )}
                      <div
                        className="absolute top-4 left-4 w-10 h-10 rounded-sm flex items-center justify-center font-black text-lg z-10"
                        style={{ background: ORANGE, color: '#FFFFFF' }}
                      >
                        {app.num}
                      </div>
                    </div>
                    <div className={`p-8 flex flex-col justify-center ${idx % 2 === 1 ? 'lg:order-1' : ''}`}>
                      <p className="text-[10px] font-bold tracking-widest uppercase mb-2" style={{ color: ORANGE }}>{app.titleEn}</p>
                      <h3 className="text-xl font-black mb-4">{app.title}</h3>
                      <p className="text-sm leading-relaxed mb-6" style={{ color: MUTED_TEXT }}>{app.desc}</p>
                      <div>
                        <p className="text-xs font-bold tracking-wider uppercase mb-3" style={{ color: MUTED_TEXT }}>典型用途</p>
                        <div className="grid grid-cols-2 gap-2">
                          {app.details.map((d) => (
                            <div key={d} className="flex items-center gap-2 text-xs">
                              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: ORANGE }} />
                              {d}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── NR vs 主流合成橡胶对比 ── */}
      <section className="py-16" style={{ background: DEEP_BG }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>MATERIAL COMPARISON</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">NR 与主流工业橡胶性能对比</h2>
              <p className="text-sm text-center mt-2 max-w-2xl mx-auto" style={{ color: MUTED_TEXT }}>
                NR 在耐磨性、回弹性和低温柔性方面具有天然优势，但需注意其耐油性和耐候性局限，选型时结合工况综合判断。
              </p>
            </motion.div>

            <motion.div variants={fadeUp} className="overflow-x-auto rounded" style={{ border: `1px solid ${BORDER}` }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: '#1A2337', borderBottom: `2px solid ${BORDER}` }}>
                    {['性能维度', 'NR 天然橡胶', 'NBR 丁腈橡胶', 'EPDM 三元乙丙', 'FKM 氟橡胶'].map((h, i) => (
                      <th key={h} className="px-5 py-3.5 text-left text-xs font-black tracking-widest uppercase"
                        style={{ color: i === 1 ? ORANGE : 'oklch(0.70 0.015 225)' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((row, i) => (
                    <tr key={row.prop} style={{ background: i % 2 === 0 ? CARD_BG : '#F0F2F5', borderBottom: `1px solid ${BORDER}` }}>
                      <td className="px-5 py-3.5 font-semibold">{row.prop}</td>
                      <td className="px-5 py-3.5 font-bold" style={{ color: ORANGE }}>{row.nr}</td>
                      <td className="px-5 py-3.5" style={{ color: MUTED_TEXT }}>{row.nbr}</td>
                      <td className="px-5 py-3.5" style={{ color: MUTED_TEXT }}>{row.epdm}</td>
                      <td className="px-5 py-3.5" style={{ color: MUTED_TEXT }}>{row.fkm}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── 定制配方 + 规格 ── */}
      <section className="py-16 lg:py-20" style={{ background: '#F0F2F5' }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>CUSTOM FORMULATION & SPECS</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">定制配方能力与产品规格</h2>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              {/* 定制表 */}
              <motion.div variants={fadeUp}>
                <h3 className="text-sm font-bold uppercase tracking-widest mb-4" style={{ color: MUTED_TEXT }}>可调整配方维度</h3>
                <div className="overflow-x-auto rounded" style={{ border: `1px solid ${BORDER}` }}>
                  <table className="w-full text-xs">
                    <thead>
                      <tr style={{ background: '#1A2337', borderBottom: `2px solid ${BORDER}` }}>
                        {['调整维度', '标准值', '可调方向'].map((h) => (
                          <th key={h} className="px-4 py-3 text-left font-black tracking-widest uppercase" style={{ color: ORANGE }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {customFormulas.map((row, i) => (
                        <tr key={row.dim} style={{ background: i % 2 === 0 ? CARD_BG : '#F0F2F5', borderBottom: `1px solid ${BORDER}` }}>
                          <td className="px-4 py-3 font-semibold">{row.dim}</td>
                          <td className="px-4 py-3 font-mono" style={{ color: 'oklch(0.78 0.012 225)' }}>{row.standard}</td>
                          <td className="px-4 py-3 font-bold" style={{ color: ORANGE }}>{row.custom}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>

              {/* 规格 + 图 */}
              <motion.div variants={fadeUp} className="flex flex-col gap-6">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded overflow-hidden" style={{ border: `1px solid ${BORDER}` }}>
                    <img src={IMAGES.P15_NR_ELASTIC_1_189} alt="NR弹性测试" className="w-full h-40 object-cover" />
                  </div>
                  <div className="rounded overflow-hidden" style={{ border: `1px solid ${BORDER}` }}>
                    <img src={IMAGES.P16_TENSILE_NR_1_200} alt="NR拉伸测试" className="w-full h-40 object-cover" />
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-widest mb-4" style={{ color: MUTED_TEXT }}>标准产品规格</h3>
                  <div className="space-y-2">
                    {specList.map((spec) => (
                      <div
                        key={spec.label}
                        className="flex items-start gap-4 text-sm py-2.5"
                        style={{ borderBottom: `1px solid ${BORDER}` }}
                      >
                        <span className="w-28 flex-shrink-0 text-xs font-bold tracking-wider uppercase pt-0.5" style={{ color: MUTED_TEXT }}>
                          {spec.label}
                        </span>
                        <span className="font-semibold">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 选型提示 ── */}
      <section className="py-10" style={{ background: DEEP_BG }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div
              variants={fadeUp}
              className="rounded p-6 flex flex-col md:flex-row gap-5 items-start"
              style={{ background: CARD_BG, border: `1px solid oklch(0.66 0.19 42 / 0.3)` }}
            >
              <div
                className="w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5"
                style={{ background: 'oklch(0.66 0.19 42 / 0.15)', border: `1px solid ${ORANGE}` }}
              >
                <Anchor className="w-5 h-5" style={{ color: ORANGE }} />
              </div>
              <div>
                <p className="text-sm font-bold mb-2" style={{ color: ORANGE }}>NR 选型重要提示</p>
                <p className="text-sm leading-relaxed" style={{ color: MUTED_TEXT }}>
                  NR 不耐石油基油脂、臭氧及紫外线长期照射，<strong className="text-foreground">户外曝晒或接触润滑油的场合应选 EPDM 或 NBR</strong>。
                  矿井内使用时需注意阻燃要求，应选择专用阻燃配方。
                  在同时需要耐高温（150°C+）和耐化学品的工况下，请优先考虑 FKM 氟橡胶。
                  如需针对您的工况进行选型评估，请联系我们的技术支持团队。
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-14" style={{ background: '#F0F2F5', borderTop: `1px solid ${BORDER}` }}>
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={stagger}
            className="max-w-3xl mx-auto text-center"
          >
            <motion.div variants={fadeUp} className="mb-4">
              <span className="text-xs font-bold tracking-widest uppercase" style={{ color: ORANGE }}>GET A QUOTE</span>
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-2xl lg:text-3xl font-black mb-4">
              <>需要 NR 高耐磨天然橡胶板？<br />我们提供技术选型与样品支持</>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-sm mb-8" style={{ color: MUTED_TEXT }}>
              说明您的工况（硬度要求、输送物料类型、规格尺寸），技术顾问将为您提供选型建议并安排样品。
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to={ROUTE_PATHS.CONTACT}
                className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-black rounded-sm"
                style={{ background: ORANGE, color: '#FFFFFF' }}
              >
                <Mail className="w-4 h-4" />
                提交询价
              </Link>
              <a
                href="tel:13426031477"
                className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-bold rounded-sm"
                style={{ border: `1px solid ${BORDER}`, color: 'oklch(0.88 0.008 220)' }}
              >
                <Phone className="w-4 h-4" />
                13426031477
              </a>
              <Link
                to={ROUTE_PATHS.SELECTION}
                className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-bold rounded-sm"
                style={{ border: `1px solid ${BORDER}`, color: 'oklch(0.88 0.008 220)' }}
              >
                <Download className="w-4 h-4" />
                查看选型矩阵
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

    </div>
  );
}
